import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Close,
  CheckCircle
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { useSnackbar } from '../../contexts/NotificationContext';
import { ProcessingStatusDialog } from './ProcessingStatusDialog';

interface MarketReportDropzoneProps {
  marketId: number;
  marketName: string;
  onProcessingComplete: () => void;
}

export const MarketReportDropzone: React.FC<MarketReportDropzoneProps> = ({
  marketId,
  marketName,
  onProcessingComplete
}) => {
  const { showSnackbar } = useSnackbar();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  
  const [file, setFile] = useState<File | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/api/marketcompass/admin/markets/${marketId}/process-report`,
        formData
      );
      
      return response.data;
    },
    onSuccess: (data) => {
      setOperationId(data.operationId);
      setStatusDialogOpen(true);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to process report';
      showSnackbar(errorMessage, 'error');
      setFile(null);
    }
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    // Validate file type
    if (!droppedFile.name.toLowerCase().endsWith('.pdf')) {
      showSnackbar('Only PDF files are allowed', 'error');
      return;
    }

    // Validate file size (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (droppedFile.size > maxSize) {
      showSnackbar('File size must be less than 50MB', 'error');
      return;
    }

    // Check if filename contains YYYYMM pattern
    const hasMonthPattern = /\d{6}/.test(droppedFile.name) || 
                           /\d{4}[-_]\d{2}/.test(droppedFile.name) ||
                           /(january|february|march|april|may|june|july|august|september|october|november|december)[-_]?\d{4}/i.test(droppedFile.name);
    
    if (!hasMonthPattern) {
      showSnackbar(
        'Filename must contain a date in YYYYMM format (e.g., 202503, 2025-03, or March_2025)',
        'warning'
      );
    }

    setFile(droppedFile);
    uploadMutation.mutate(droppedFile);
  }, [marketId, showSnackbar, uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    disabled: uploadMutation.isPending
  });

  const handleRemoveFile = () => {
    setFile(null);
    uploadMutation.reset();
  };

  const handleProcessingComplete = () => {
    // Just notify parent that processing is complete
    // Don't close the dialog - let the user do that
    onProcessingComplete();
  };
  
  const handleDialogClose = () => {
    setStatusDialogOpen(false);
    setFile(null);
    setOperationId(null);
    uploadMutation.reset();
  };

  return (
    <>
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
      >
        <input {...getInputProps()} />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          {uploadMutation.isPending ? (
            <CircularProgress size={48} sx={{ mb: 2 }} />
          ) : file ? (
            <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
          ) : (
            <CloudUpload sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
          )}

          <Typography variant="h6" gutterBottom>
            {uploadMutation.isPending
              ? 'Uploading...'
              : file
              ? 'File uploaded successfully'
              : 'Drop New MarketCompass Report'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {uploadMutation.isPending
              ? 'Please wait while we process your report'
              : file
              ? `Processing: ${file.name}`
              : 'Drag and drop a PDF file here, or click to select'}
          </Typography>

          {file && !uploadMutation.isPending && (
            <Fade in>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsertDriveFile color="action" />
                <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Fade>
          )}

          {!uploadMutation.isPending && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 500 }}>
              <Typography variant="body2">
                The report will be automatically processed to generate descriptions, key findings, and a podcast for {marketName}.
                Make sure the filename contains the report month in YYYYMM format.
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>

      {operationId && (
        <ProcessingStatusDialog
          open={statusDialogOpen}
          operationId={operationId}
          onClose={handleDialogClose}
          onComplete={handleProcessingComplete}
        />
      )}
    </>
  );
};